// Deps
import { createComponent } from "react-fela";
import { useState, useEffect } from "react";
import { fluid } from "~/styleguide/utils";
import Router from "next/router";
import { AnimatePresence } from "framer-motion";
import { colors } from "~/styleguide";
// Component
import WordpressLink from "~/components/global/links";
import SubNav from "./subnav";
import Icon from "~/components/shared/icon";

// Data
import headerMenu from "~/data/header-menu.json";

export default function MainNavigation({ className }) {
    const { data: menu } = headerMenu;

    // Overview copy for the subnav
    //const { data: data } = globalData;
    //const overviews = get(data, "acf.subnav_overview", []);

    const [activeIndex, setActiveIndex] = useState(null);

    /**
     * Listen to document clicks and close the menu if clicking outside
     * of the Subnav panel
     */
    useEffect(() => {
        //Close subnav on clicks outside the nav
        const onDocumentClick = e => {
            if (
                activeIndex !== null &&
                !e.target.closest(".subnav") &&
                !e.target.closest(".primary-navigation-parent")
            )
                closeSubnav();
        };
        document.addEventListener("click", onDocumentClick, false);

        //Close subnav on document scroll
        const onDocumentScroll = () => {
            if(activeIndex !== null)
                closeSubnav();
        };
        document.addEventListener("scroll", onDocumentScroll, false);


        // Ensure that the menu closes when going to any page
        Router.events.on("routeChangeStart", closeSubnav);

        // Unbind method when component dismounts
        return function cleanup() {
            document.removeEventListener("click", onDocumentClick, false);
            document.removeEventListener("scroll", onDocumentScroll, false);
            Router.events.off("routeChangeStart", closeSubnav);
        };
    });

    const closeSubnav = () => {
        setActiveIndex(null);
    };

    /**
     * Determine if the parent menu item has children. If so, render a toggleaable
     * entry that will open/close the subnav. Otherwise, render a simple link.
     *
     * @param {Object} item Wordpress menu entry
     * @param {Integer} index Index of the loop
     */
    const renderTopLevelItem = (item, index) => {
        let isActive = activeIndex === index ? true : false;
        const hasChildren = item.child_items && item.child_items.length;

        return (
            <div
                className={`nav-dropdown ${isActive ? "active" : ""}`}
                onMouseEnter={() => {
                    // Set the active subnav index, or none, if clicking the same subnav
                    if(!isActive){
                        setActiveIndex(hasChildren ? index : null);
                        isActive = true;
                    } else {
                        setActiveIndex(null);
                        isActive = false;
                    }
                }}
                onMouseLeave={() => {
                    closeSubnav();
                }}
            >
            {hasChildren && (
                <NavDropdownItem
                    className="nav-dropdown-item"
                    extend={headerTopLevelStyles(isActive)}
                >
                <span>{item.title}</span>
                        <>
                            {" "}
                            <Icon
                                name="menu-arrow-full"
                                styles={iconStyles(isActive)}
                            />
                        </>
                </NavDropdownItem>
            )}
            {!hasChildren && (
                <WordpressLink
                    url={item.url}
                    label={item.title}
                    extend={headerTopLevelStyles(isActive)}
                />
            )}
            {hasChildren && isActive && (
                <AnimatePresence>
                        <SubNav
                            className="subnav"
                            activeIndex={index}
                            menuData={menu.items}
                        />
                </AnimatePresence>
            )}
        </div>
        );
    };

    return (
        <Container className={className}>
            {/* Render top-level navigation items */}
            {menu && menu.items && (
                <ul className="primary-navigation">
                    {menu.items.map((item, index) => {
                        return (
                            <li
                                key={item.ID}
                                className="primary-navigation-parent"
                            >
                                {renderTopLevelItem(item, index)}
                            </li>
                        );
                    })}
                </ul>
            )}
        </Container>
    );
}

const iconStyles = isActive => ({
    fontSize: 7,
    marginLeft: 6,
    verticalAlign: "middle",
    extend: [
        {
            condition: isActive,
            style: {
                transform: "rotate(90deg)"
            }
        }
    ]
});

const hoverStyles = {
    color: colors.blue
    // fontWeight: "bold"
};

const _NavItemContainer = isActive => ({
    extend:[
        {
            condition: isActive,
            style:{
                color: colors.red
            }
        }
    ]

})

const headerTopLevelStyles = isActive => ({
    fontWeight: "normal",
    color: "#000",
    ":visited": {
        color: "inherit!important"
    },
    whenActive: hoverStyles,
    extend: [
        {
            condition: isActive,
            style: {
                color: colors.blue
            }
        }
    ]
});

const NavDropdownItem = createComponent(
    () =>({
    })
);

const Container = createComponent(
    () => ({
        zIndex: 0,
        "> .primary-navigation": {
            padding: 0,
            position: "relative",
            "& li": {
                lineHeight: "70px",
            },
            "& .nav-dropdown-item": {
                cursor: "pointer",
            }
        },

        "& .primary-navigation-parent": {
            display: "inline-block",
            extend: [fluid("paddingH", { max: 17, min: 5 })],
            "& .nav-dropdown": {
                position: "relative",
            },
            "&:nth-child(2)": {
                "& .nav-dropdown": {
                    position: "unset",
                }
            }
        },

        minWidthDesktop: {
            position: "absolute",
            width: "100%",
            height: "100%",
            right: 0,
            top: -7,
            bottom: 0,
            margin: "auto",
            textAlign: "center"
        }
    }),
    "nav",
    ["className"]
);
